import { CountryField } from '../../shared/country_field/country_field.js';
import { FieldShell } from '../../shared/field/field_shell.js';
import { StaticSelectField } from '../../shared/field/static_select_field.js';
import { personInitials } from '../../shared/person/person_name.js';
import { resolveCostCenter, type AllocationDraft } from '../fields/allocation.js';
import { BirthdateField } from '../fields/birthdate_field.js';
import {
  DocumentField,
  type DocumentDraft,
  type DocumentMaskedInfo,
} from '../fields/document_field.js';
import { EmbarkField } from '../fields/embark_field.js';
import { GenderField } from '../fields/gender_field.js';
import { MotiveField } from '../fields/motive_field.js';
import { ObservationField } from '../fields/observation_field.js';
import { PhoneField } from '../fields/phone_field.js';
import { useCheckoutCountries } from '../form/use_checkout_countries.js';
import type { CheckoutRules } from '../model/checkout_env.js';
import type {
  CheckoutBookingForm,
  CheckoutField,
  CheckoutTravelerDraft,
} from '../model/checkout_source.js';
import {
  CheckoutTravelerCard,
  type CheckoutTravelerAllocation,
} from '../traveler/checkout_traveler_card.js';
import { useCheckoutEnv } from './env_context.js';
import { maskDocumentValue, travelerChips } from './traveler_chips.js';
import { formatTravelerDate } from '../fields/date_copy.js';

/** Nome do centro de custo escolhido — alimenta o farol de orçamento ("Limite
 *  suficiente em {{costCenter}}…"). Sem escolha, sem nome. */
export function costCenterNameOf(
  form: CheckoutBookingForm,
  allocation: AllocationDraft,
): string | undefined {
  // Passa por `resolveCostCenter` (e não pelo catálogo direto) para também
  // enxergar a escolha vinda da busca remota, que não está no form.
  return resolveCostCenter(form, allocation)?.name;
}

function updateDocument(
  rules: CheckoutRules,
  current: CheckoutTravelerDraft,
  doc: DocumentDraft,
): CheckoutTravelerDraft {
  // CPF on file is read-only (the submit mapper strips it: the backend resolves it
  // by traveler id); without one the typed CPF goes on the wire (#5809), masked as
  // it is typed (#5935) — the submit mapper sends the digits only.
  if (doc.type === 'cpf') {
    return { ...current, document: { type: 'cpf', value: rules.maskCpf(doc.value) } };
  }
  return {
    ...current,
    document: {
      type: doc.type,
      value: doc.value,
      issuingCountry: doc.issuingCountry,
      expirationDate: doc.expirationDate,
    },
  };
}

export interface TravelerFieldItemProps {
  readonly field: CheckoutField;
  readonly traveler: CheckoutBookingForm['traveler'];
  readonly travelerDraft: CheckoutTravelerDraft;
  readonly idPrefix?: string;
  readonly onUpdateTraveler: (patch: CheckoutTravelerDraft) => void;
}

function maskedDocumentValueFor(
  type: 'cpf' | 'passport' | 'dni',
  traveler: CheckoutBookingForm['traveler'],
): string | undefined {
  const masked = traveler?.maskedInfo?.[type];
  if (masked) return masked;
  return traveler?.document?.type === type ? maskDocumentValue(traveler.document.value) : undefined;
}

/** Os 11 dígitos de um CPF digitado com verificador errado (#5935). Incompleto
 *  ainda não é erro: a pessoa está digitando, e o rodapé já pede o documento. */
function typedCpfInvalid(rules: CheckoutRules, document: DocumentDraft | undefined): boolean {
  if (document?.type !== 'cpf') return false;
  const digits = document.value.replace(/\D/g, '');
  return digits.length === 11 && !rules.isValidCpf(digits);
}

function BirthdateFieldItem({
  traveler,
  travelerDraft,
  idPrefix,
  onUpdateTraveler,
}: Omit<TravelerFieldItemProps, 'field'>) {
  const { locale, copy: text } = useCheckoutEnv();
  const copy = text.fields;
  return (
    <BirthdateField
      copy={copy.birthdate}
      dateCopy={copy.date}
      locale={locale}
      value={travelerDraft.birthdate ?? traveler?.birthdate}
      onChange={(birthdate) => {
        onUpdateTraveler({ birthdate });
      }}
      readOnly={Boolean(traveler?.birthdate)}
      displayValue={formatTravelerDate(traveler?.birthdate, locale)}
      idPrefix={idPrefix}
    />
  );
}

function EmbarkFieldItem({
  traveler,
  travelerDraft,
  idPrefix,
  onUpdateTraveler,
}: Omit<TravelerFieldItemProps, 'field'>) {
  const copy = useCheckoutEnv().copy.fields;
  return (
    <EmbarkField
      copy={copy.embark}
      name={travelerDraft.boardingName ?? traveler?.firstName}
      surname={travelerDraft.boardingSurname ?? traveler?.lastName}
      onChange={onUpdateTraveler}
      idPrefix={idPrefix}
    />
  );
}

function DocumentFieldItem({
  field,
  traveler,
  travelerDraft,
  idPrefix,
  onUpdateTraveler,
}: TravelerFieldItemProps & { readonly field: Extract<CheckoutField, { kind: 'document' }> }) {
  const env = useCheckoutEnv();
  const copy = env.copy.fields;
  const countries = useCheckoutCountries(env.io, env.locale);
  const onFileDoc = traveler?.document;
  const documentForField: DocumentDraft | undefined =
    travelerDraft.document ?? (onFileDoc ? { type: onFileDoc.type, value: '' } : undefined);
  const documentMasked: DocumentMaskedInfo = {
    cpf: maskedDocumentValueFor('cpf', traveler),
    passport: maskedDocumentValueFor('passport', traveler),
    passportCountry: traveler?.maskedInfo?.passportCountry,
    passportValidate: traveler?.maskedInfo?.passportValidate,
    dni: maskedDocumentValueFor('dni', traveler),
  };
  return (
    <DocumentField
      document={documentForField}
      defaultIssuingCountry={traveler?.nationality?.code}
      maskedInfo={documentMasked}
      allow={field.allow}
      cpfOnFile={Boolean(documentMasked.cpf)}
      cpfInvalid={!documentMasked.cpf && typedCpfInvalid(env.rules, documentForField)}
      idPrefix={idPrefix}
      countries={countries.countries}
      countriesState={countries.state}
      onCountriesRetry={countries.reload}
      copy={copy.document}
      dateCopy={copy.date}
      locale={env.locale}
      onChange={(document) => {
        onUpdateTraveler(updateDocument(env.rules, travelerDraft, document));
      }}
    />
  );
}

/** A nacionalidade no `CountryField` do catálogo do perfil (#3669): sem
 *  catálogo, o valor gravado fica só-leitura. */
function NationalityFieldItem({
  traveler,
  travelerDraft,
  idPrefix,
  onUpdateTraveler,
}: Omit<TravelerFieldItemProps, 'field'>) {
  const env = useCheckoutEnv();
  const copy = env.copy.fields;
  const countries = useCheckoutCountries(env.io, env.locale);
  const fieldId = `${idPrefix ?? ''}pc-nationality`;
  const nationality = travelerDraft.nationality ?? traveler?.nationality;
  const code = nationality?.code;
  return (
    <FieldShell label={copy.nationality.label} fieldId={fieldId}>
      <CountryField
        id={fieldId}
        {...(code ? { value: { code, name: nationality.name ?? '' } } : {})}
        countries={countries.countries}
        state={countries.state}
        copy={copy.nationality}
        onRetry={countries.reload}
        onChange={(next) => {
          onUpdateTraveler({ nationality: { code: next.code, name: next.name } });
        }}
      />
    </FieldShell>
  );
}

/** A ordem dos campos de identidade no drawer e no card 1click: o nome de embarque
 *  abre (linha inteira), os curtos (nascimento, nacionalidade, sexo, telefone) dividem
 *  as fileiras de dois na ordem do manifesto, e o resto (o documento, com país
 *  emissor/validade) fecha, cada um numa linha própria. */
export function identityRows<T extends CheckoutField>(fields: readonly T[]) {
  const isShort = (field: CheckoutField) =>
    field.kind === 'birthdate' ||
    field.kind === 'nationality' ||
    field.kind === 'gender' ||
    field.kind === 'phone';
  const isLead = (field: CheckoutField) => field.kind === 'embark';
  return {
    lead: fields.filter(isLead),
    paired: fields.filter(isShort),
    tail: fields.filter((field) => !isLead(field) && !isShort(field)),
  };
}

/** Um campo de identidade do manifesto, ligado ao rascunho do viajante — o drawer
 *  o monta no cartão do viajante; o card 1click, em «Complete seus dados». */
export function TravelerFieldItem(props: TravelerFieldItemProps) {
  const { field, traveler, travelerDraft, idPrefix, onUpdateTraveler } = props;
  const copy = useCheckoutEnv().copy.fields;
  switch (field.kind) {
    case 'birthdate':
      return <BirthdateFieldItem {...props} />;
    case 'nationality':
      return <NationalityFieldItem {...props} />;
    case 'document':
      return <DocumentFieldItem {...props} field={field} />;
    case 'gender':
      return (
        <GenderField
          copy={copy.gender}
          value={travelerDraft.gender ?? traveler?.gender}
          onChange={(gender) => {
            onUpdateTraveler({ gender });
          }}
        />
      );
    case 'embark':
      return <EmbarkFieldItem {...props} />;
    case 'phone':
      return (
        <PhoneField
          copy={copy.phone}
          value={travelerDraft.phone}
          onChange={(phone) => {
            onUpdateTraveler({ phone });
          }}
          idPrefix={idPrefix}
        />
      );
    case 'allocation':
    case 'observation':
    case 'select':
    default:
      return null;
  }
}

export interface TravelerCardProps {
  /** Os campos de identidade do manifesto (grupo `traveler`). */
  fields: CheckoutField[];
  traveler: CheckoutBookingForm['traveler'];
  editing: boolean;
  travelerDraft: CheckoutTravelerDraft;
  /** Prefixo dos ids dos campos — único por viajante (evita ids duplicados entre
   *  os cartões). */
  idPrefix: string;
  /** Nome do roster enquanto o form carrega. */
  fallbackName: string;
  /** Badge "Você" ao lado do nome (Figma 405-5518). */
  isRequester: boolean;
  /** Ausente enquanto o form do viajante carrega. */
  allocation?: CheckoutTravelerAllocation;
  onToggleEditing: (v: boolean) => void;
  onUpdateTraveler: (patch: CheckoutTravelerDraft) => void;
}

/** O cartão do viajante: o `CheckoutTravelerCard` do intelligence-ui-kit com os
 *  campos de identidade do manifesto, ligados ao rascunho. Sem campo de identidade
 *  (o manifesto do pedido à equipe, `CONCIERGE_FIELDS`) fica só o cabeçalho, que
 *  ainda diz de quem é o cartão. */
export function TravelerCard({
  fields,
  traveler,
  editing,
  travelerDraft,
  idPrefix,
  fallbackName,
  isRequester,
  allocation,
  onToggleEditing,
  onUpdateTraveler,
}: Readonly<TravelerCardProps>) {
  const { copy: text, locale } = useCheckoutEnv();
  const copy = text.fields;
  const labels = text.traveler;
  const name = traveler?.name ?? fallbackName;
  const rows = identityRows(fields);
  const fieldItem = (field: CheckoutField) => (
    <TravelerFieldItem
      key={field.kind}
      field={field}
      traveler={traveler}
      travelerDraft={travelerDraft}
      idPrefix={idPrefix}
      onUpdateTraveler={onUpdateTraveler}
    />
  );
  return (
    <CheckoutTravelerCard
      name={name}
      {...(name ? { initials: personInitials(name) } : {})}
      isRequester={isRequester}
      chips={travelerChips(fields, traveler, travelerDraft, locale)}
      editable={fields.length > 0}
      editing={editing}
      onToggleEditing={onToggleEditing}
      leadFields={rows.lead.map(fieldItem)}
      pairedFields={rows.paired.map(fieldItem)}
      fields={rows.tail.map(fieldItem)}
      {...(allocation ? { allocation } : {})}
      {...(fields.length > 0 ? { docMatchNote: copy.docMatchNote } : {})}
      copy={{
        you: labels.you,
        edit: labels.edit,
        done: labels.done,
        genderFemale: labels.genderFemale,
        genderMale: labels.genderMale,
        phonePending: labels.phonePending,
        inheritedHint: labels.inheritedHint,
        inheritedHintOther: labels.inheritedHintOther,
      }}
    />
  );
}

export interface CorporateFieldItemProps {
  /** Um campo da reserva: motivo, justificativa ou observação. O rateio é por
   *  viajante e vive no cartão dele. */
  field: CheckoutField;
  form: CheckoutBookingForm;
  /** Fora da política a justificativa deixa de ser opcional — o `canSubmit` já
   *  exigia, faltava o campo dizer. */
  withinPolicy: boolean;
  motiveId: string;
  justificationId: string | undefined;
  observation: string;
  onMotiveChange: (v: string) => void;
  onJustificationChange: (v: string | undefined) => void;
  onObservationChange: (v: string) => void;
}

export function CorporateFieldItem({
  field,
  form,
  withinPolicy,
  motiveId,
  justificationId,
  observation,
  onMotiveChange,
  onJustificationChange,
  onObservationChange,
}: Readonly<CorporateFieldItemProps>) {
  const { copy: text } = useCheckoutEnv();
  const copy = text.fields;
  if (field.kind === 'select' && field.id === 'motive') {
    return (
      <MotiveField
        motives={form.motives}
        value={motiveId}
        onChange={onMotiveChange}
        copy={copy.motive}
      />
    );
  }
  if (field.kind === 'select' && field.id === 'justification' && form.justifications.length > 0) {
    // "(opcional)" vive no LABEL (Figma), não no placeholder — fora da política
    // a justificativa é obrigatória e o sufixo some.
    return (
      <StaticSelectField
        label={withinPolicy ? text.traveler.justificationOptional : text.traveler.justification}
        value={justificationId ?? ''}
        options={form.justifications.map((j) => ({ value: j.id, label: j.name }))}
        onChange={onJustificationChange}
        placeholder={text.traveler.selectPlaceholder}
      />
    );
  }
  if (field.kind === 'observation') {
    return (
      <ObservationField
        copy={copy.observation}
        value={observation}
        maxLength={field.maxLength}
        onChange={onObservationChange}
      />
    );
  }
  return null;
}
